import { Injectable } from '@angular/core';
import { App, AppKey, GetMultiAppRequest, PartnerApiService } from '@vendasta/marketplace-apps';
import { MarketplacePackagesApiService, Package } from '@vendasta/marketplace-packages';
import { Observable, of } from 'rxjs';
import { map, shareReplay, switchMap, withLatestFrom } from 'rxjs/operators';
import { InventoryItem, ToInventoryItem } from '../common/inventory-item';
import { areEditionIdsEqual, mapify } from '../item-selector/util';

@Injectable()
export class PackageContentsService {
  constructor(
    private readonly marketplacePackagesApiService: MarketplacePackagesApiService,
    private readonly partnerApiService: PartnerApiService,
  ) {}

  fetchPackageItems(packageId: string, partnerId: string, marketId: string): Observable<InventoryItem[]> {
    return this.fetchMultiPackageItems([packageId], partnerId, marketId).pipe(
      map((packageItemsMap) => packageItemsMap.get(packageId) ?? []),
    );
  }

  fetchMultiPackageItems(
    packageIds: string[],
    partnerId: string,
    marketId: string,
  ): Observable<Map<string, InventoryItem[]>> {
    if (!packageIds.length) {
      return of(new Map());
    }

    const packages$ = this.getPackages(packageIds);
    const lineItems$ = packages$.pipe(switchMap((packages) => this.getLineItems(packages, partnerId, marketId)));
    return lineItems$.pipe(
      withLatestFrom(packages$),
      map(([lineItems, packages]) => {
        const packageItemsMap = mapify(
          packages,
          (p) => p.packageId,
          (p) => this.transformPackageLineItems(p, lineItems),
        );
        return packageItemsMap;
      }),
    );
  }

  private getPackages(packageIds: string[]): Observable<Package[]> {
    return this.marketplacePackagesApiService.getMultiPackages({ packageIds: packageIds }).pipe(
      map((resp) => resp.packageContainers.map((p) => p.package)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  private getLineItems(packages: Package[], partnerId: string, marketId: string): Observable<App[]> {
    const getMultiAppRequest = new GetMultiAppRequest({
      projectionFilter: {
        paths: ['editionInformation', 'sharedMarketingInformation'],
      },
      appKeys: packages
        .map((p) => p.lineItems.lineItems)
        .flat()
        .map((lineItem) => {
          return new AppKey({
            appId: lineItem.id,
            editionId: lineItem.editionId || '',
          });
        }),
      partnerId: partnerId,
      marketId: marketId,
    });
    return this.partnerApiService.getMultiApp(getMultiAppRequest).pipe(
      map((resp) => resp?.apps ?? []),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  private transformPackageLineItems(packageItem: Package, lineItems: App[]): InventoryItem[] {
    const packageLineItems = packageItem.lineItems.lineItems;
    const transformedLineItems = packageLineItems.map((pli) => {
      const lineItem = lineItems.find(
        (li) => li.key.appId === pli.id && areEditionIdsEqual(li.key.editionId, pli.editionId),
      )!;
      return { ...ToInventoryItem<App>(lineItem), quantity: pli?.quantity ?? 1 };
    });
    return transformedLineItems;
  }
}
